import { useState, useRef, useEffect } from 'react';
import axios from "axios";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalBody,
} from "@chakra-ui/modal";
import { Button } from '@chakra-ui/react';
import { MdSecurity, MdOutlineContentCopy } from "react-icons/md";
import { ImSpinner8 } from "react-icons/im";
import { Auth } from "aws-amplify";
import QRCode from "react-qr-code";
import { Tooltip, IconButton } from "@mui/material";

const appName = process.env.REACT_APP_NAME!;

const TwoFactorSetupModal: React.FC<{
    setSwalProps: any;
    isOpen: boolean;
    onClose(): void;
    onSuccess(): void;
    userRole: string;
    username: string;
    userEmail: string;
}> = ({
    setSwalProps,
    isOpen,
    onClose,
    onSuccess,
    userRole,
    username,
    userEmail
}) => {
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [isSettingUp, setIsSettingUp] = useState<boolean>(true);
    const [totpSecret, setTotpSecret] = useState<string>("");
    const [otpAuthUrl, setOtpAuthUrl] = useState<string>("");
    const [showSecret, setShowSecret] = useState<boolean>(false);
    const [otp, setOtp] = useState<string[]>(Array(6).fill(""));
    const [showVerifyOTPError, setShowVerifyOTPError] = useState<boolean>(false);
    const [showVerifyOTPErrorByUseOnce, setShowVerifyOTPErrorByUseOnce] = useState<boolean>(false);
    const otpInputRefs = useRef<HTMLInputElement[]>([]);
    const hasInitialized = useRef<boolean>(false);

    const isOTPFormValid = otp.every(digit => digit !== "");

    useEffect(() => {
        if (isOpen && !hasInitialized.current) {
            hasInitialized.current = true;
            initializeSetup();
        }
        if (!isOpen) {
            hasInitialized.current = false;
        }
    }, [isOpen]);

    useEffect(() => {
        const otpToVerify = otp.join("");
        if (String(otpToVerify).length === 6 && !isProcessing) {
            handleVerify();
        }
    }, [otp]);

    const initializeSetup = async () => {
        try {
            setIsSettingUp(true);
            setShowSecret(false);
            setOtp(Array(6).fill(""));
            setShowVerifyOTPError(false);
            setShowVerifyOTPErrorByUseOnce(false);

            const user = await Auth.currentAuthenticatedUser();
            const secret = await Auth.setupTOTP(user);

            setTotpSecret(String(secret));
            generateQRCode(userEmail, String(secret));

            // Add delay for better UX
            await new Promise(resolve => setTimeout(resolve, 1000));
            setIsSettingUp(false);
        } catch (error) {
            console.error("Error setting up TOTP:", error);
            setIsSettingUp(false);
            closeModal();
            setSwalProps({
                show: true,
                icon: "error",
                title: "Oops!",
                html: "Failed to set up two-factor authentication. Please try again.",
                showConfirmButton: true,
                didClose: () => {
                    setSwalProps({});
                },
            });
        }
    };

    const generateQRCode = (email: string, secret: string) => {
        const otpAuthUrl = `otpauth://totp/${encodeURIComponent(appName)}:${encodeURIComponent(email)}?secret=${secret}&issuer=${encodeURIComponent(appName)}`;
        setOtpAuthUrl(otpAuthUrl);
    };

    const closeModal = () => {
        setOtp(Array(6).fill(""));
        setShowVerifyOTPError(false);
        setShowVerifyOTPErrorByUseOnce(false);
        setShowSecret(false);
        setIsProcessing(false);        
        onClose();
    };

    const handleVerify = async () => {
        if (isProcessing) return;

        try {
            setIsProcessing(true);
            setShowVerifyOTPError(false);
            setShowVerifyOTPErrorByUseOnce(false);

            const otpToVerify = otp.join("");
            const user = await Auth.currentAuthenticatedUser();

            // Verify the TOTP token
            await Auth.verifyTotpToken(user, otpToVerify);

            // Call API to enable 2FA and mark setup complete
            await axios.post(
                `${process.env.REACT_APP_API_URL}/api/${userRole.toLowerCase()}/user/action/user-profile-update-two-factor-status`,
                {
                    username: username,
                    status: "enabled",
                    userRole: userRole,
                }
            );

            // Close modal first, then show success message
            setOtp(Array(6).fill(""));
            setShowVerifyOTPError(false);
            setShowVerifyOTPErrorByUseOnce(false);
            setShowSecret(false);
            setIsProcessing(false);
            onSuccess();
            onClose();

            setSwalProps({
                show: true,
                icon: "success",
                title: "Success",
                html: "Two-factor authentication has been enabled successfully.",
                showConfirmButton: true,
                didClose: () => {
                    setSwalProps({});
                },
            });
        } catch (error: any) {
            setIsProcessing(false);

            if (String(error.message).includes("once")) {
                setShowVerifyOTPErrorByUseOnce(true);
            } else {
                setShowVerifyOTPError(true);
            }
            console.error("Error verifying TOTP:", error);
        }
    };

    const handleClickCopy = async (secret: string) => {
        try {
            await navigator.clipboard.writeText(secret);
        } catch (err) {
            console.error("Failed to copy secret: ", err);
        }
    };

    const handleOTPInputChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        index: number
    ) => {
        setShowVerifyOTPError(false);
        setShowVerifyOTPErrorByUseOnce(false);
        const { value } = e.target;
        if (/^\d*$/.test(value)) {
            const newOtp = [...otp];
            newOtp[index] = value;
            setOtp(newOtp);

            if (value && index < 5) {
                otpInputRefs.current[index + 1].focus();
            } else if (index === 5) {
                otpInputRefs.current[index].blur();
            }
        }
    };

    const handleOTPInputKeyDown = (
        e: React.KeyboardEvent<HTMLInputElement>,
        index: number
    ) => {
        setShowVerifyOTPError(false);
        setShowVerifyOTPErrorByUseOnce(false);
        if (e.key === "Backspace") {
            if (otp[index] === "" && index > 0) {
                otpInputRefs.current[index - 1].focus();
            } else {
                const newOtp = [...otp];
                newOtp[index] = "";
                setOtp(newOtp);
            }
        }
    };

    const handleOTPInputPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        setShowVerifyOTPError(false);
        setShowVerifyOTPErrorByUseOnce(false);
        e.preventDefault();
        const pasteData = e.clipboardData.getData("text").slice(0, 6);
        const newOtp = pasteData
            .split("")
            .map((char, i) => (i < 6 ? char : otp[i]));
        setOtp(newOtp);

        pasteData.split("").forEach((_, i) => {
            if (i < 5 && pasteData[i]) {
                otpInputRefs.current[i + 1].focus();
            } else if (i === 5) {
                otpInputRefs.current[i].blur();
            }
        });
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
            <ModalOverlay className="bg-[#000] !opacity-30" />
            <ModalContent className="dark:!bg-neutral-900 !z-[1002] !m-auto !w-full max-w-xl !rounded-2xl !border !border-gray-200 !bg-white !shadow-xl dark:!border-white/10 md:!top-[12vh]">
                <ModalBody className="p-6 md:p-7">
                    <div className="mb-4 flex items-start gap-3">
                        <div className="rounded-xl bg-primary-50 p-2 text-primary-600 ring-1 ring-primary-100 dark:bg-primary-900/20 dark:text-primary-400 dark:ring-primary-900/30">
                            <MdSecurity size={20} />
                        </div>
                        <div>
                            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Set Up Two-Factor Authentication
                            </h1>
                            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                                Scan the QR code with your authenticator app
                            </p>
                        </div>
                    </div>

                    {isSettingUp ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="text-center">
                                <div className="mb-4 flex justify-center">
                                    <ImSpinner8 className="spinner text-primary-500" size={40} />
                                </div>
                                <div className="text-base font-medium text-navy-700 dark:text-white">
                                    Setting up two-factor authentication...
                                </div>
                                <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                    Please wait a moment
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 dark:border-blue-900/40 dark:bg-blue-900/20 dark:text-blue-300">
                                Use an authentication app like <strong>Authy</strong> or <strong>Google Authenticator</strong> to scan the QR code below.
                            </div>

                            {/* QR Code */}
                            <div className="flex items-center justify-center rounded-xl p-1 mb-4">
                                <QRCode
                                    value={otpAuthUrl}
                                    size={140}
                                    className="border-1 m-2 rounded-md border-gray-200 bg-white p-3 shadow-md"
                                />
                            </div>

                            {/* Secret Key */}
                            <div className="mb-4 text-center text-sm">
                                <div className="mb-2 text-navy-700 dark:text-white">
                                    Or manually enter the secret key using <strong>Time-based (TOTP)</strong>
                                </div>
                                {!showSecret ? (
                                    <span
                                        className="cursor-pointer text-sm font-semibold text-primary-600"
                                        onClick={() => setShowSecret(true)}
                                    >
                                        Click to show the secret
                                    </span>
                                ) : (
                                    <div className="flex w-full items-center justify-center space-x-2">
                                        <input
                                            readOnly
                                            className="h-8 w-full max-w-xs rounded-md text-center text-xs font-semibold text-navy-700 outline-none bg-gray-50 dark:bg-neutral-800 dark:text-white"
                                            value={totpSecret}
                                        />
                                        <Tooltip title="Copy Secret" arrow placement="top">
                                            <IconButton
                                                onClick={() => handleClickCopy(totpSecret)}
                                            >
                                                <MdOutlineContentCopy className="h-5 w-5 text-navy-800 dark:text-white" />
                                            </IconButton>
                                        </Tooltip>
                                    </div>
                                )}
                            </div>

                            {/* OTP Input */}
                            <div className="mb-4">
                                <p className="mb-3 text-center text-sm text-navy-700 dark:text-white">
                                    Please wait up to 30 seconds for a new code to be generated, then enter it below
                                </p>
                                <div className="flex w-full items-center justify-center space-x-2">
                                    {otp.map((digit, index) => (
                                        <input
                                            key={index}
                                            ref={(el) => {
                                                if (el) otpInputRefs.current[index] = el;
                                            }}
                                            type="text"
                                            maxLength={1}
                                            value={digit}
                                            disabled={isProcessing}
                                            onChange={(e) => handleOTPInputChange(e, index)}
                                            onKeyDown={(e) => handleOTPInputKeyDown(e, index)}
                                            onPaste={handleOTPInputPaste}
                                            className="h-12 w-10 rounded-lg border border-gray-300 text-center shadow-sm outline-none transition-all focus:border-primary-500 focus:ring-1 focus:ring-primary-400 dark:border-white/10 dark:bg-neutral-800 dark:text-white"
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Error Messages */}
                            {showVerifyOTPError && (
                                <p className="mb-4 text-center text-sm text-red-500">
                                    Invalid code, please try again
                                </p>
                            )}

                            {showVerifyOTPErrorByUseOnce && (
                                <p className="mb-4 text-center text-sm text-red-500">
                                    Invalid code, it has already been used
                                </p>
                            )}

                            <div className="flex justify-center gap-2">
                                <Button
                                    onClick={closeModal}
                                    isDisabled={isProcessing}
                                    className="btn btn-default"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleVerify}
                                    isDisabled={!isOTPFormValid || isProcessing}
                                    isLoading={isProcessing}
                                    loadingText='Verifying...'
                                    className="btn btn-primary"
                                >
                                    Verify & Enable
                                </Button>
                            </div>
                        </>
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default TwoFactorSetupModal;
